// NewPosts.js

import React, { useContext, useEffect, useMemo, useState } from 'react';
import LooksPost from './LooksPost';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { FaSpinner } from 'react-icons/fa';
import './HomePage.css';
import {
  Box,
  Fade,
  List,
  ListItem,
  Paper,
  Typography,
  IconButton,
  useMediaQuery
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import RefreshIcon from '@mui/icons-material/Refresh';
import VisibilityTracker from './VisibilityTracker';

const MAX_RAW = 120;                 // teaser cut-off

const NewPosts = () => {
  /* ---------- context ---------- */
  const {
    posts,                // already timestamp-sorted in provider
    fetchMorePosts,
    loading,
    hasMore,
    error,
    deletePostById
  } = useContext(LooksPostsContext);

  const navigate = useNavigate();

  /* ---------- local state ---------- */
  const [randomPostsKey, setRandomPostsKey] = useState(0);
  const [clickedPosts, setClickedPosts] = useState(() => {
    const saved = localStorage.getItem('clickedPosts');
    return saved ? JSON.parse(saved) : {};
  });

  /* ---------- responsive helpers ---------- */
  const isSmallScreen      = useMediaQuery('(max-width: 768px)');
  const isVerySmallScreen  = useMediaQuery('(max-width: 480px)');
  const isIPhoneSESize     = useMediaQuery('(max-width: 375px)');

  const postContainerStyle = useMemo(
    () => ({
      width: '100%',
      maxWidth: '800px',
      margin: '0 auto 15px',
      padding: 0,
      boxSizing: 'border-box'
    }),
    []
  );

  /* ---------- lifecycle ---------- */
  useEffect(() => window.scrollTo(0, 0), []);

  /* infinite scroll */
  useEffect(() => {
    const handleScroll = () => {
      if (
        window.innerHeight + window.scrollY >= document.body.scrollHeight - 200 &&
        !loading && hasMore
      ) {
        fetchMorePosts();
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, [loading, hasMore, fetchMorePosts]);

  /* keep clicked-state synced across tabs */
  useEffect(() => {
    const handleStorage = () => {
      const saved = localStorage.getItem('clickedPosts');
      if (saved) setClickedPosts(JSON.parse(saved));
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  /* ---------- Discover-Posts pool (shuffled each refresh) ---------- */
  const randomPosts = useMemo(() => {
    const valid = posts.filter(p => !p.deleted && !p.isReported);
    const shuffled = [...valid].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 3);
  }, [posts, randomPostsKey]);

  const handleRefresh = () => setRandomPostsKey(k => k + 1);

  const handlePostClick = (postId) => {
    const updated = { ...clickedPosts, [postId]: true };
    setClickedPosts(updated);
    localStorage.setItem('clickedPosts', JSON.stringify(updated));
    navigate(`/looksposts/${postId}`);
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    try { await deletePostById(postId); }
    catch (err) { console.error('Error deleting post:', err); }
  };

  /* ---------- early-return states ---------- */
  if (loading) {
    return (
      <Box sx={{ display:'flex', justifyContent:'center', mt:3 }}>
        <FaSpinner className="loading-spinner" size={24} spin />
      </Box>
    );
  }
  if (error)  {
    return <Box sx={{ textAlign:'center', mt:3, color:'red' }}>{error}</Box>;
  }
  if (!posts || posts.length === 0) {
    return <Box sx={{ textAlign:'center', mt:3 }}>No new posts found.</Box>;
  }

  /* ---------- render ---------- */
  return (
    <Box
      sx={{
        display:'flex',
        flexDirection:'column',
        alignItems:'center',
        p:2,
        pt:{ xs:10, sm:12, md:15, lg:8, xl:8 }
      }}
      className="posts"
    >
      {posts.map((post, index) => (
        <React.Fragment key={post.id}>
          <div className="post-container" style={postContainerStyle}>
            <LooksPost {...post} onDelete={() => handleDelete(post.id)} />
          </div>

          {/* Discover-Posts block every 3 items (same cadence as current) */}
          {(index + 1) % 3 === 0 && index + 1 < posts.length && (
            <Fade in timeout={800}>
              <Paper
                elevation={3}
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  p: { xs:2, sm:3, md:4 },
                  pt: { xs:2, sm:3, md:3 },
                  pb: { xs:1, sm:2, md:3 },
                  mt: 4,
                  width: '100%',
                  maxWidth: '800px',
                  margin: '0 auto',
                  mb: 2,
                  borderRadius: '16px',
                  backgroundColor: '#fff',
                  overflow: 'hidden'
                }}
              >
                {/* ---- title + refresh row ---- */}
                <Box
                  sx={{
                    display:'flex',
                    alignItems:'center',
                    justifyContent:'space-between',
                    width:'100%',
                    mb:{ xs:1, sm:2, md:2 },
                    gap:{ xs:1, sm:2 }
                  }}
                >
                  <Typography
                    variant={isVerySmallScreen ? 'h5' : 'h4'}
                    sx={{
                      fontWeight:700,
                      color:'#1d1d1f',
                      lineHeight:1.2,
                      fontSize:{ xs:'1.3rem', sm:'1.5rem', md:'1.8rem' },
                      maxWidth:{ xs:'70%', sm:'80%' }
                    }}
                  >
                    Discover Posts
                  </Typography>
                  <IconButton
                    onClick={handleRefresh}
                    sx={{
                      color:'#007aff',
                      p:{ xs:'6px', sm:'8px' },
                      backgroundColor:'#f5f5f7',
                      '&:hover':{ backgroundColor:'#e8e8ed' },
                      transition:'background-color 0.3s ease',
                      flexShrink:0
                    }}
                  >
                    <RefreshIcon fontSize={isIPhoneSESize ? 'small' : 'medium'} />
                  </IconButton>
                </Box>

                {/* ---- list of three shuffled posts ---- */}
                <List sx={{ width: '100%', p: 0 }}>
                  {randomPosts.map((rPost, idx) => {
                    const raw = (rPost.description ?? '').trim();
                    const teaser =
                      raw.length <= MAX_RAW
                        ? raw
                        : rPost.summary ?? raw.slice(0, MAX_RAW) + '…';
                    const displayName = rPost.userName || 'Anonymous';

                    return (
                      <VisibilityTracker key={rPost.id} postId={rPost.id} isDiscoveryBox>
                        <Fade in timeout={500 + idx * 100} unmountOnExit>
                          <ListItem
                            sx={{
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              p: idx === 0 ? '8px 0 16px' : '12px 0 16px',
                              borderRadius: '8px'
                            }}
                            onClick={() => handlePostClick(rPost.id)}
                          >
                            <Typography
                              variant="body1"
                              sx={{
                                color: clickedPosts[rPost.id] ? '#551A8B' : '#1a0dab',
                                cursor: 'pointer',
                                fontWeight: 'bold',
                                fontSize: '1.1rem',
                                mb: '2px',
                                '&:hover': { textDecoration: 'underline' }
                              }}
                            >
                              {displayName}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ color: '#1d1d1f', fontSize: '0.95rem', mt: 0.5 }}
                            >
                              {teaser}
                            </Typography>
                          </ListItem>
                        </Fade>
                      </VisibilityTracker>
                    );
                  })}
                </List>
              </Paper>
            </Fade>
          )}
        </React.Fragment>
      ))}

      {/* bottom loading spinner for infinite scroll */}
      {loading && (
        <Box sx={{ display:'flex', justifyContent:'center', mt:3 }}>
          <FaSpinner className="loading-spinner" size={24} spin />
        </Box>
      )}
    </Box>
  );
};

export default NewPosts;
